/** Les rubriques textuelles de la fiche formation (#319), dans l'ordre où on les lit. */
export const SHEET_TEXT_FIELDS = ['prerequisites', 'audience', 'teachingMethods', 'assessmentMethods', 'accessibility'] as const

export type SheetTextField = typeof SHEET_TEXT_FIELDS[number]

/** La fiche telle que l'API la rend. */
export type TrainingSheet = Partial<{ objectives: string[] | null, contact: string | null, positioningEnabled: boolean | null } & Record<SheetTextField, string | null>>

/** La fiche telle que le formulaire l'édite : un objectif par ligne. */
export type SheetForm = { objectives: string, contact: string, positioningEnabled: boolean } & Record<SheetTextField, string>

/** Bornes du formulaire, les mêmes que l'API (`training-sheet.ts`). */
export const SHEET_LIMITS = { objectives: 20, objective: 300, text: 2000, contact: 300 } as const

export function sheetFormFrom(sheet?: TrainingSheet | null): SheetForm {
  return {
    objectives: (sheet?.objectives ?? []).join('\n'),
    contact: sheet?.contact ?? '',
    positioningEnabled: sheet?.positioningEnabled ?? false,
    ...Object.fromEntries(SHEET_TEXT_FIELDS.map(field => [field, sheet?.[field] ?? ''])) as Record<SheetTextField, string>,
  }
}

/** Le formulaire, prêt pour l'API : une ligne vide n'est pas un objectif. */
export function toSheetInput(form: SheetForm) {
  const objectives = sheetObjectives(form)
  // Sans objectif, rien sur quoi se positionner (#321) : l'API refuserait.
  return { ...form, objectives, positioningEnabled: form.positioningEnabled && objectives.length > 0 }
}

/** Les objectifs saisis, une ligne vide n'en étant pas un. */
export function sheetObjectives(form: Pick<SheetForm, 'objectives'>): string[] {
  return form.objectives.split('\n').map(line => line.trim()).filter(Boolean)
}

/** Une fiche sans rien à lire ne s'affiche pas : un bloc vide ne dirait rien à l'apprenant. */
export function isSheetEmpty(sheet: TrainingSheet): boolean {
  return !(sheet.objectives?.length) && !sheet.contact && SHEET_TEXT_FIELDS.every(field => !sheet[field])
}
